import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EChartsStockChart, { ChartSeries } from '../../../components/EChartsStockChart';
import { getRentabilidadeAtivo } from '../../../services/carteiraDiariaService';
import { RentabilidadeAtivoResponse } from '../../../types/carteiraDiaria';
import StatBox from '../../../components/StatBox';

const toTimestamp = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const rentInfoLabel = (data: RentabilidadeAtivoResponse): string => {
  const ri = data.rent_info;
  if (!ri || ri.rent_valor == null) return 'Esperada';
  if (ri.rent_tipo === 'FIXO')        return `${ri.rent_valor}% ${ri.rent_periodo_display ?? ''}`.trim();
  if (ri.rent_tipo === 'IND_PERCENT') return `${ri.rent_valor}% do ${ri.rent_indicador_codigo ?? ''}`.trim();
  if (ri.rent_tipo === 'IND_SPREAD')  return `${ri.rent_indicador_codigo ?? ''} + ${ri.rent_valor}% a.a.`.trim();
  return 'Esperada';
};

const INFO_TEXT = [
  'Retorno acumulado desde a primeira posição registrada neste fundo.',
  '',
  'Cálculo diário:',
  '  ln( (cota_hoje + amort_por_cota) / cota_ontem )',
  '',
  'Funciona para qualquer tipo de ativo. Para ativos sem eventos de',
  'amortização, amort_por_cota = 0 e o cálculo é simplesmente',
  'ln(cota_hoje / cota_ontem).',
  '',
  'Amortizações e pagamentos de juros são registrados no histórico de',
  'movimentações como total R$ recebido pelo fundo. O valor por cota',
  'é obtido dividindo pelo número de cotas do dia.',
  '',
  'A linha esperada requer indicadores sincronizados (sync_indicadores)',
  'para tipos IND_PERCENT / IND_SPREAD (ex: CDI).',
].join('\n');

export interface RentabilidadeAtivoSectionProps {
  fundoId: number;
  codigo1: string;
  adm: string;
  nome: string;
}

const RentabilidadeAtivoSection: React.FC<RentabilidadeAtivoSectionProps> = ({ fundoId, codigo1, adm, nome }) => {
  const [data, setData] = useState<RentabilidadeAtivoResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getRentabilidadeAtivo(fundoId, codigo1, adm, nome)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [fundoId, codigo1, adm, nome]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!data || data.realizada.length === 0) {
    return (
      <Typography sx={{ py: 3, color: '#8A98A8', textAlign: 'center', fontSize: '13px' }}>
        Sem histórico de rentabilidade disponível para este ativo.
      </Typography>
    );
  }

  const ultimoRealizado = data.realizada[data.realizada.length - 1].acumulado;
  const ultimoEsperado  = data.esperada ? data.esperada[data.esperada.length - 1].acumulado : null;
  const diff = ultimoEsperado !== null ? ultimoRealizado - ultimoEsperado : null;
  const label = rentInfoLabel(data);

  const chartSeries: ChartSeries[] = [
    {
      name: 'Realizada',
      data: data.realizada.map(p => [toTimestamp(p.data), p.acumulado]),
      color: '#2C91AD',
    },
  ];
  if (data.esperada) {
    chartSeries.push({
      name: label,
      data: data.esperada.map(p => [toTimestamp(p.data), p.acumulado]),
      color: '#F5A623',
      lineStyle: { type: 'dashed', width: 2 },
    });
  }

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 1, backgroundColor: '#F8FAFC' }}>
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" alignItems="center">
        <StatBox
          label="Retorno Realizado"
          value={`${ultimoRealizado >= 0 ? '+' : ''}${ultimoRealizado.toFixed(2)}%`}
          valueColor={ultimoRealizado >= 0 ? '#29A152' : '#E51739'}
        />
        {ultimoEsperado !== null && (
          <StatBox
            label={`Esperado (${label})`}
            value={`${ultimoEsperado >= 0 ? '+' : ''}${ultimoEsperado.toFixed(2)}%`}
            valueColor="#F5A623"
          />
        )}
        {diff !== null && (
          <StatBox
            label="vs. Esperado"
            value={`${diff >= 0 ? '+' : ''}${diff.toFixed(2)}%`}
            valueColor={diff >= 0 ? '#29A152' : '#E51739'}
          />
        )}
        <Tooltip
          title={<Box component="pre" sx={{ fontSize: '11px', fontFamily: 'Inter, monospace', whiteSpace: 'pre-wrap', m: 0 }}>{INFO_TEXT}</Box>}
          placement="right"
          arrow
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#8A98A8', cursor: 'help', ml: 0.5 }} />
        </Tooltip>
      </Stack>

      <EChartsStockChart
        series={chartSeries}
        yAxisTitle="%"
        suffix="%"
        height={420}
        simplifiedXAxis={false}
        rebasable
      />
    </Box>
  );
};

export default RentabilidadeAtivoSection;
